import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../shared/components/dialog';
import { RMSTextField, RMSSelect, RMSCheckbox } from '../../shared/components/form-controls';
import CreateFormResolver from '../../shared/utility/createFormResolver';
import { executeApiAction } from '../../shared/utility/apiHelper';
import useConfirmDialog from '../../shared/utility/useConfirmDialog';
import { notify } from 'reducers/NotifierReducer';
import { ZONE_DEFAULT_VALUES, ZONE_TEXT_FIELDS } from '../constants/zoneFormConstants';
import zoneSchema from '../schema/zoneSchema';
import { getZoneAgencies, findDuplicateZones, createZone } from '../services/zoneService';
import { formatCodeZones } from '../utility/zoneFormHelper';
import { hideZoneDialog, zoneDialogSaved } from '../store/actions/zoneDialog.actions';

const AddZoneDialog = ({ dialog }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agencies, setAgencies] = useState([]);

  const methods = useForm({
    defaultValues: ZONE_DEFAULT_VALUES,
    resolver: CreateFormResolver(zoneSchema),
    mode: 'onBlur',
    disabled: loading || submitting,
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const close = useCallback(() => dispatch(hideZoneDialog(dialog.requestId)), [
    dispatch,
    dialog.requestId,
  ]);

  const requestClose = () => {
    if (submitting) return;
    if (isDirty)
      requestConfirm('You have unsaved changes. Close without saving?', close, 'Unsaved Changes');
    else close();
  };

  const save = async (values) => {
    if (submitting || loading) return;
    await executeApiAction({
      dispatch,
      setLoading: setSubmitting,
      action: async () => {
        const duplicates = await findDuplicateZones(values.ZoneCode);
        if (duplicates.length) {
          methods.setError(
            'ZoneCode',
            { type: 'server', message: 'This Zone Code already exists. Enter a different code.' },
            { shouldFocus: true }
          );
          return null;
        }
        return createZone(formatCodeZones(values));
      },
      onSuccess: (zone) => {
        if (!zone) return;
        dispatch(notify('Zone created successfully', 'success'));
        dispatch(zoneDialogSaved(zone, dialog.requestId));
        close();
      },
    });
  };

  useEffect(() => {
    let active = true;
    executeApiAction({
      dispatch,
      action: getZoneAgencies,
      setLoading: (value) => {
        if (active) setLoading(value);
      },
      onSuccess: (rows) => {
        if (active)
          setAgencies(
            [...new Set(rows.map((row) => row.AgencyID).filter(Boolean))].map((value) => ({
              value,
              label: value,
            }))
          );
      },
    });
    return () => {
      active = false;
    };
  }, [dispatch]);

  return (
    <RMSCommonDialog
      open
      title="Add Zone"
      onClose={requestClose}
      onSave={methods.handleSubmit(save)}
      loading={loading || submitting}
      saveDisabled={loading || submitting}
      maxWidth="md"
      fullWidth
      closeOnBackdropClick={false}>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <RMSSelect name="AgencyID" label="Agency" options={agencies} />
          </Grid>
          {ZONE_TEXT_FIELDS.map((field) => (
            <Grid item xs={12} sm={6} key={field.name}>
              <RMSTextField {...field} />
            </Grid>
          ))}
          <Grid item xs={6}>
            <RMSCheckbox name="IsDefault" label="Default" />
          </Grid>
          <Grid item xs={6}>
            <RMSCheckbox name="IsActive" label="Active" />
          </Grid>
        </Grid>
      </FormProvider>
      {confirmState.open && (
        <RMSConfirmationDialog
          open
          title={confirmState.title}
          message={confirmState.message}
          onYes={handleYes}
          onNo={handleNo}
        />
      )}
    </RMSCommonDialog>
  );
};
export default AddZoneDialog;
